import React, { useCallback, useRef, useState } from 'react';
import { Arrow, Circle, Group, Line, Rect, Text } from 'react-konva';
import Konva from 'konva';
import type { KonvaDragEvent, KonvaMouseEvent, Node, Point, ThemePalette } from '../../types';
import { FONT_FAMILY } from '../../constants/typography';
import { EDGE } from '../../constants/edges';
import { arrowFromPoints, fitTextNodeSize, localArrowPoints, markupEdgeType } from '../../utils/markup';
import { isDrawCascadeModifier, isStackableDrawing, symmetricHandleSize } from '../../utils/drawStack';
import { HaloText, LABEL_HALO_ALPHA } from './HaloText';
import { ResizeHandle } from './ResizeHandle';
import { toRgba } from '../../utils/color';

interface MarkupNodeComponentProps {
  node: Node;
  isSelected: boolean;
  isReadOnly?: boolean;
  canDrag?: boolean;
  hideLabel?: boolean;
  layerOpacity?: number;
  palette: ThemePalette;
  onUpdate: (id: string, updates: Partial<Node>) => void;
  onNodeClick: (nodeId: string, shiftKey?: boolean) => void;
  onNodeDoubleClick?: (nodeId: string) => void;
  onLabelDoubleClick?: (nodeId: string) => void;
  onNodeContextMenu?: (nodeId: string, x: number, y: number) => void;
  onNodeHover?: (nodeId: string | null) => void;
  onNodeResize?: (nodeId: string, width: number, height: number, options?: { fromCenter?: boolean; cascade?: boolean }) => void;
  onMultiDragStart?: (nodeId: string) => void;
  onMultiDragMove?: (nodeId: string, dx: number, dy: number) => void;
  onMultiDragEnd?: (nodeId: string, dx: number, dy: number) => void;
}

export const MarkupNodeComponent: React.FC<MarkupNodeComponentProps> = ({
  node,
  isSelected,
  isReadOnly = false,
  canDrag,
  hideLabel = false,
  layerOpacity = 1,
  palette,
  onUpdate,
  onNodeClick,
  onNodeDoubleClick,
  onLabelDoubleClick,
  onNodeContextMenu,
  onNodeHover,
  onNodeResize,
  onMultiDragStart,
  onMultiDragMove,
  onMultiDragEnd,
}) => {
  const w = node.size.width;
  const h = node.size.height;
  const accentColor = (typeof node.data?.color === 'string' ? node.data.color : null) ?? '#94a3b8';
  const canDragNode = canDrag ?? !isReadOnly;
  const groupRef = useRef<Konva.Group>(null);
  const dragOrigin = useRef<{ x: number; y: number } | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [strokeDrag, setStrokeDrag] = useState<{ start: Point; end: Point } | null>(null);
  const resizeOrigin = useRef<{ w: number; h: number } | null>(null);

  const handleClick = useCallback((e: KonvaMouseEvent) => {
    e.cancelBubble = true;
    onNodeClick(node.id, e.evt.shiftKey);
  }, [node.id, onNodeClick]);

  const handleDblClick = useCallback((e: KonvaMouseEvent) => {
    e.cancelBubble = true;
    onNodeDoubleClick?.(node.id);
  }, [node.id, onNodeDoubleClick]);

  const handleLabelDblClick = useCallback((e: KonvaMouseEvent) => {
    e.cancelBubble = true;
    onLabelDoubleClick?.(node.id);
  }, [node.id, onLabelDoubleClick]);

  const handleContextMenu = useCallback((e: KonvaMouseEvent) => {
    e.cancelBubble = true;
    e.evt.preventDefault();
    onNodeContextMenu?.(node.id, e.evt.clientX, e.evt.clientY);
  }, [node.id, onNodeContextMenu]);

  const handleDragStart = useCallback((e: KonvaDragEvent) => {
    dragOrigin.current = { x: e.target.x(), y: e.target.y() };
    onMultiDragStart?.(node.id);
  }, [node.id, onMultiDragStart]);

  const handleDragMove = useCallback((e: KonvaDragEvent) => {
    const x = e.target.x();
    const y = e.target.y();
    if (dragOrigin.current && onMultiDragMove) {
      onMultiDragMove(node.id, x - dragOrigin.current.x, y - dragOrigin.current.y);
    }
    onUpdate(node.id, { position: { x, y } });
  }, [node.id, onUpdate, onMultiDragMove]);

  const handleDragEnd = useCallback((e: KonvaDragEvent) => {
    const x = e.target.x();
    const y = e.target.y();
    if (dragOrigin.current && onMultiDragEnd) {
      onMultiDragEnd(node.id, x - dragOrigin.current.x, y - dragOrigin.current.y);
    }
    dragOrigin.current = null;
    onUpdate(node.id, { position: { x, y } });
  }, [node.id, onUpdate, onMultiDragEnd]);

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
    onNodeHover?.(node.id);
  }, [node.id, onNodeHover]);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
    onNodeHover?.(null);
  }, [onNodeHover]);

  const isStroke = node.type === 'arrow' || node.type === 'line';
  const textMin = node.type === 'text' ? fitTextNodeSize(node.label || '', 40) : null;
  const minW = textMin?.width ?? (node.type === 'text' ? 40 : 80);
  const minH = textMin?.height ?? (node.type === 'text' ? 24 : 60);
  const labelHalo = toRgba(palette.canvasBg, LABEL_HALO_ALPHA);

  const clampMarkupSize = (rawW: number, rawH: number, proportional: boolean) => {
    let newW = Math.max(minW, rawW);
    let newH = Math.max(minH, rawH);
    if (proportional && resizeOrigin.current) {
      const origin = resizeOrigin.current;
      const scale = Math.max(newW / origin.w, newH / origin.h);
      newW = Math.max(minW, Math.round(origin.w * scale));
      newH = Math.max(minH, Math.round(origin.h * scale));
    }
    return { newW, newH };
  };
  const localPts = strokeDrag ?? localArrowPoints(node);
  const edgeType = markupEdgeType(node);
  const strokeWidth = node.data?.strokeWidth ?? 2;
  const dash = EDGE.DASH_PATTERNS[node.data?.dashStyle ?? 'solid'];

  const commitStrokeEnd = useCallback((which: 'start' | 'end', local: Point) => {
    const startLocal = which === 'start' ? local : localPts.start;
    const endLocal = which === 'end' ? local : localPts.end;
    const absStart = { x: node.position.x + startLocal.x, y: node.position.y + startLocal.y };
    const absEnd = { x: node.position.x + endLocal.x, y: node.position.y + endLocal.y };
    const geom = arrowFromPoints(absStart, absEnd);
    onUpdate(node.id, {
      position: geom.position,
      size: geom.size,
      data: { ...node.data, from: geom.from, to: geom.to },
    });
    setStrokeDrag(null);
  }, [localPts.start, localPts.end, node.data, node.id, node.position.x, node.position.y, onUpdate]);

  const resizeHandle = !isReadOnly && !isStroke && onNodeResize ? (
    <ResizeHandle
      nodeWidth={w}
      nodeHeight={h}
      accentColor={accentColor}
      selected={isSelected}
      nudgeId={isStackableDrawing(node.type) ? 'resize-drawing' : 'resize-shape'}
      idleHint={false}
      onDragStart={() => {
        resizeOrigin.current = { w: node.size.width, h: node.size.height };
      }}
      onDragEnd={() => {
        resizeOrigin.current = null;
      }}
      onResize={(rawW, rawH, mods) => {
        const stackable = isStackableDrawing(node.type);
        const fromCenter = mods.altKey;
        const cascade = stackable && isDrawCascadeModifier(mods);
        let width = rawW;
        let height = rawH;
        if (fromCenter && resizeOrigin.current) {
          const symmetric = symmetricHandleSize(resizeOrigin.current, rawW, rawH, { x: true, y: true });
          width = symmetric.width;
          height = symmetric.height;
        }
        const { newW, newH } = clampMarkupSize(width, height, mods.shiftKey);
        onNodeResize(node.id, newW, newH, { fromCenter, cascade });
        return { width: newW, height: newH };
      }}
    />
  ) : null;

  const endpointHandle = (which: 'start' | 'end', pt: Point) => (
    <Circle
      key={which}
      x={pt.x}
      y={pt.y}
      radius={6}
      fill={palette.nodeBody}
      stroke={accentColor}
      strokeWidth={1.5}
      draggable
      onMouseDown={(e) => { e.cancelBubble = true; }}
      onDragStart={(e) => { e.cancelBubble = true; }}
      onDragMove={(e) => {
        e.cancelBubble = true;
        const next = { x: e.target.x(), y: e.target.y() };
        setStrokeDrag(which === 'start'
          ? { start: next, end: localPts.end }
          : { start: localPts.start, end: next });
      }}
      onDragEnd={(e) => {
        e.cancelBubble = true;
        commitStrokeEnd(which, { x: e.target.x(), y: e.target.y() });
      }}
      perfectDrawEnabled={false}
    />
  );

  return (
    <Group
      ref={groupRef}
      x={node.position.x}
      y={node.position.y}
      opacity={layerOpacity}
      draggable={canDragNode}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragEnd={handleDragEnd}
      onClick={handleClick}
      onDblClick={handleDblClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onContextMenu={handleContextMenu}
    >
      {node.type === 'text' && (
        <>
          <Rect
            width={w}
            height={h}
            fill={isSelected || isHovered ? accentColor + '14' : 'transparent'}
            stroke={isSelected ? accentColor : 'transparent'}
            strokeWidth={isSelected ? 1 : 0}
            dash={[4, 3]}
            cornerRadius={4}
            perfectDrawEnabled={false}
          />
          {!hideLabel && node.label && (
            <HaloText
              x={4} y={4}
              width={Math.max(8, w - 8)}
              height={Math.max(8, h - 8)}
              text={node.label}
              fontSize={16}
              fontFamily={FONT_FAMILY}
              fontStyle="500"
              fill={accentColor === '#e2e8f0' ? palette.nodeText : accentColor}
              wrap="word"
              haloFill={labelHalo}
              lineHeightPx={22}
              padX={5}
              padY={3}
              onDblClick={handleLabelDblClick}
            />
          )}
          {resizeHandle}
        </>
      )}

      {node.type === 'note' && (
        <>
          <Rect
            x={2} y={3} width={w} height={h}
            fill={palette.nodeShadow}
            cornerRadius={6}
            listening={false}
            perfectDrawEnabled={false}
          />
          <Rect
            width={w}
            height={h}
            fill={accentColor + '28'}
            stroke={isSelected ? accentColor : accentColor + '99'}
            strokeWidth={isSelected ? 1.5 : 1}
            cornerRadius={6}
            perfectDrawEnabled={false}
          />
          <Line
            points={[w - 16, 0, w, 0, w, 16]}
            closed
            fill={accentColor + '55'}
            listening={false}
            perfectDrawEnabled={false}
          />
          {!hideLabel && (
            <Text
              x={10} y={12}
              width={Math.max(8, w - 20)}
              height={Math.max(8, h - 22)}
              text={node.label}
              fontSize={13}
              fontFamily={FONT_FAMILY}
              fill={palette.nodeText}
              wrap="word"
              lineHeight={18 / 13}
              onDblClick={handleLabelDblClick}
              perfectDrawEnabled={false}
            />
          )}
          {resizeHandle}
        </>
      )}

      {isStroke && (
        <>
          <Arrow
            points={[localPts.start.x, localPts.start.y, localPts.end.x, localPts.end.y]}
            stroke={accentColor}
            fill={accentColor}
            strokeWidth={strokeWidth}
            pointerLength={edgeType === 'no-arrow' ? 0 : 12}
            pointerWidth={edgeType === 'no-arrow' ? 0 : 10}
            pointerAtBeginning={edgeType === 'bidirectional'}
            pointerAtEnding={edgeType !== 'no-arrow'}
            dash={dash}
            hitStrokeWidth={16}
            perfectDrawEnabled={false}
          />
          {!hideLabel && node.label && (
            <HaloText
              x={(localPts.start.x + localPts.end.x) / 2 - 48}
              y={(localPts.start.y + localPts.end.y) / 2 - 12}
              width={96}
              text={node.label}
              fontSize={11}
              fontFamily={FONT_FAMILY}
              fill={palette.nodeText}
              align="center"
              haloFill={labelHalo}
              lineHeightPx={15}
              onDblClick={handleLabelDblClick}
            />
          )}
          {!isReadOnly && isSelected && (
            <>
              {endpointHandle('start', localPts.start)}
              {endpointHandle('end', localPts.end)}
            </>
          )}
        </>
      )}
    </Group>
  );
};
